import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  APP_LANGUAGES,
  getLanguageConfig,
  nextAppLanguage,
  readStoredLanguage,
  writeStoredLanguage,
} from './languages';
import { translate, type TranslateParams } from './translate';
import type { MessageKey } from './messages';
import type { AppLanguage, AppLanguageConfig } from './types';

type LanguageContextValue = {
  language: AppLanguage;
  config: AppLanguageConfig;
  languages: readonly AppLanguageConfig[];
  setLanguage: (code: AppLanguage) => void;
  toggleLanguage: () => void;
  t: (key: MessageKey, params?: TranslateParams) => string;
};

const LanguageContext = createContext<LanguageContextValue | null>(null);

function applyDocumentLanguage(config: AppLanguageConfig): void {
  const root = document.documentElement;
  root.lang = config.code;
  root.dir = config.dir;
  document.body.classList.toggle('lang-he', config.code === 'he');
}

type LanguageProviderProps = { children: ReactNode };

export function LanguageProvider({ children }: LanguageProviderProps) {
  const [language, setLanguageState] = useState<AppLanguage>(() => readStoredLanguage());

  const config = useMemo(() => getLanguageConfig(language), [language]);

  useEffect(() => {
    applyDocumentLanguage(config);
  }, [config]);

  const setLanguage = useCallback((code: AppLanguage) => {
    setLanguageState(code);
    writeStoredLanguage(code);
  }, []);

  const toggleLanguage = useCallback(() => {
    setLanguageState((current) => {
      const next = nextAppLanguage(current);
      writeStoredLanguage(next);
      return next;
    });
  }, []);

  const t = useCallback(
    (key: MessageKey, params?: TranslateParams) => translate(language, key, params),
    [language],
  );

  const value = useMemo<LanguageContextValue>(
    () => ({
      language,
      config,
      languages: APP_LANGUAGES,
      setLanguage,
      toggleLanguage,
      t,
    }),
    [language, config, setLanguage, toggleLanguage, t],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return ctx;
}

/** Shorthand for `useLanguage().t`. */
export function useI18n() {
  const { t, language, setLanguage, toggleLanguage, config, languages } = useLanguage();
  return { t, language, setLanguage, toggleLanguage, config, languages };
}
